import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus, UserCog, Ban, Mail, Copy, Trash2, KeyRound, Pencil } from 'lucide-react';
import type { Role, User } from '@conversia/shared-types';
import * as usersApi from '@/api/users.api';
import * as copilotoApi from '@/api/copiloto.api';
import {
  AVISO_CONFIRMAR_DUENO,
  cambioDeAcceso,
  etiquetaDeAcceso,
  opcionActual,
  opcionesDeAcceso,
  usoCorto,
  type OpcionAcceso,
} from '@/lib/copiloto-acceso';
import {
  OPCIONES_DE_ROL,
  OPCIONES_FILTRO_ESTADO,
  OPCIONES_FILTRO_ROL,
  contadorDeUsuarios,
  filtrarUsuarios,
  queHaceElRol,
  type EstadoUsuarios,
} from '@/lib/usuarios-filtros';
import { ESQUEMA_USUARIOS } from '@/lib/filtros-esquemas';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { PageHeader } from '@/components/common/PageHeader';
import { CajaDeBusqueda } from '@/components/common/CajaDeBusqueda';
import { roleLabel } from '@/lib/badges';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/store/auth.store';
import { useToast } from '@/hooks/useToast';
import { useCrmView } from '@/hooks/useCrmView';
import { Cargando } from '@/components/common/Cargando';

const ROLES_BASE = [
  { value: 'ADMIN', label: 'Administrador' },
  { value: 'AGENT', label: 'Agente' },
  { value: 'VIEWER', label: 'Lector' },
];
/**
 * El rol TÉCNICO existe en el producto, no en todos los negocios. Ofrecerlo en
 * una tienda de ropa deja crear una cuenta que al entrar solo puede ver «Sin
 * perfil de técnico»: una pantalla que no lleva a ninguna parte y una cuenta que
 * después hay que borrar.
 *
 * Se OFRECE según el módulo; no se esconde un rol que alguien ya tenga puesto
 * (eso sería perder un dato, no simplificar una pantalla).
 */
function rolesDisponibles(usaInstalaciones: boolean) {
  return usaInstalaciones
    ? [...ROLES_BASE, { value: 'TECNICO', label: 'Técnico' }]
    : ROLES_BASE;
}

/** Los filtros que esta pantalla guarda en la URL. */
interface FiltrosUsuarios {
  buscar?: string;
  rol?: string;
  estado?: EstadoUsuarios;
}

/**
 * Lo que hay que confirmar antes de hacerlo.
 *
 * Desactivar una cuenta, restablecer una clave, revocar una invitación y
 * cambiarle el rol a alguien se aplicaban de una, sin preguntar nada: un clic
 * en el icono de al lado y la persona se quedaba afuera del sistema, o con las
 * sesiones cerradas en mitad del día. Las cuatro pasan ahora por este modal.
 */
interface Confirmacion {
  titulo: string;
  cuerpo: string;
  boton: string;
  alConfirmar: () => void;
}

/**
 * Editar usuario (rediseño /users): nombre y EMAIL — para corregir typos
 * (gmai.com) sin recrear la cuenta. La unicidad la valida el backend.
 */
function EditarUsuarioModal({ user, onClose }: { user: User | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [telefono, setTelefono] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Pre-carga al abrir (user pasa de null → usuario).
  useEffect(() => {
    if (!user) return;
    setNombre(user.name ?? '');
    setEmail(user.email);
    setTelefono(user.telefono ?? '');
    setError(null);
  }, [user]);

  const save = useMutation({
    mutationFn: () =>
      usersApi.updateUser(user!.id, {
        ...(nombre.trim() && nombre.trim() !== (user!.name ?? '') ? { name: nombre.trim() } : {}),
        ...(email.trim().toLowerCase() !== user!.email ? { email: email.trim() } : {}),
        // Se manda solo si cambio. Vacio viaja como null: borrar el telefono
        // tiene que poder hacerse, y null es lo que el server guarda.
        ...(telefono.trim() !== (user!.telefono ?? '')
          ? { telefono: telefono.trim() || null }
          : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      toast.success('Usuario actualizado');
      onClose();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'No se pudo guardar el usuario'),
  });

  function submit() {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Escribe un email válido');
      return;
    }
    setError(null);
    save.mutate();
  }

  return (
    <Modal
      open={Boolean(user)}
      onClose={onClose}
      title="Editar usuario"
      size="sm"
      description="Corrige el nombre, el email de acceso o el teléfono. El email debe ser único."
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={submit} loading={save.isPending}>
            Guardar
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input label="Nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} />
        <Input
          label="Email de acceso"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <div>
          <Input
            label="Teléfono de WhatsApp"
            value={telefono}
            placeholder="809 555 1234"
            onChange={(e) => setTelefono(e.target.value)}
          />
          <p className="mt-1 text-xs text-slate-500">
            A este número le llegan los avisos de los clientes que le tocan. Sin teléfono
            cargado, el aviso no sale y el cliente se queda esperando.
          </p>
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    </Modal>
  );
}

const inviteSchema = z.object({
  email: z.string().min(1, 'El email es obligatorio').email('Email inválido'),
  role: z.enum(['ADMIN', 'AGENT', 'VIEWER', 'TECNICO']),
});
type InviteValues = z.infer<typeof inviteSchema>;

function InviteModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const {
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors },
  } = useForm<InviteValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: '', role: 'AGENT' },
  });
  const selectedRole = watch('role');
  // Mientras la config carga se asume que SÍ: ofrecer un rol de más un instante
  // es menos malo que esconderle a un negocio de instalaciones el rol que usa.
  const usaInstalaciones = useCrmView()?.osito?.modulos?.instalaciones !== false;

  const invite = useMutation({
    mutationFn: (values: InviteValues) => usersApi.invite(values),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invitations'] });
      // Sin decir mentiras: el correo solo sale si el servidor lo tiene
      // configurado; el enlace copiable de abajo funciona SIEMPRE.
      toast.success('Invitación creada — copie el enlace y mándelo por WhatsApp');
      reset();
      onClose();
    },
    onError: (err) =>
      setError('root', { message: err instanceof ApiError ? err.message : 'No se pudo invitar' }),
  });

  const onSubmit = handleSubmit((values) => invite.mutate(values));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Invitar usuario"
      description="Se creará una invitación con un enlace de registro."
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={onSubmit} loading={invite.isPending}>
            Enviar invitación
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <Input label="Email" type="email" placeholder="persona@empresa.com" error={errors.email?.message} {...register('email')} />
        <Select label="Rol" options={rolesDisponibles(usaInstalaciones)} {...register('role')} />
        {usaInstalaciones && selectedRole === 'TECNICO' && (
          <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
            La cuenta se crea cuando el técnico acepte la invitación. Falta UN paso después:
            vincularla en <span className="font-medium">Empleados → editar su ficha → «Cuenta de
            acceso»</span>. Sin ese vínculo, al entrar verá «Sin perfil de técnico».
          </p>
        )}
        {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}
      </form>
    </Modal>
  );
}

export default function Users() {
  const qc = useQueryClient();
  const toast = useToast();
  const meId = useAuthStore((s) => s.user?.id);
  const miRol = useAuthStore((s) => s.user?.role);
  // El acceso al asistente lo ven y lo cambian solo ADMIN y OWNER: la API lo
  // exige igual, pero a otro rol no se le muestra algo que no puede tocar.
  const veAccesos = miRol === 'ADMIN' || miRol === 'OWNER';
  const [inviteOpen, setInviteOpen] = useState(false);
  // Dar "Dueño" se confirma: deja ver la plata y lo que hablan los demás.
  const [duenoPorConfirmar, setDuenoPorConfirmar] = useState<User | null>(null);
  // Lo que falta confirmar antes de aplicarse (desactivar, clave, rol, revocar).
  const [confirmacion, setConfirmacion] = useState<Confirmacion | null>(null);

  // A1: los filtros viven en la URL, como en las demás pantallas. Así un enlace
  // a "los técnicos inactivos" se manda por WhatsApp y el otro ve lo mismo.
  const { filtros, setFiltros, limpiarFiltros, hayFiltros } =
    useFiltrosUrl<FiltrosUsuarios>(ESQUEMA_USUARIOS);
  const buscar = filtros.buscar ?? '';
  const rolFiltrado = filtros.rol ?? '';
  const estado = filtros.estado ?? 'todos';

  const { data: users, isLoading } = useQuery({
    queryKey: ['users', 'list'],
    queryFn: () => usersApi.listUsers({ take: 100 }),
  });
  const { data: invitations } = useQuery({
    queryKey: ['invitations'],
    queryFn: () => usersApi.listInvitations(),
  });

  const accesos = useQuery({
    queryKey: ['copiloto', 'accesos'],
    queryFn: copilotoApi.listarAccesos,
    enabled: veAccesos,
  });
  const accesoDe = (userId: string) => accesos.data?.find((a) => a.userId === userId);

  const cambiarAcceso = useMutation({
    mutationFn: async ({ userId, opcion }: { userId: string; opcion: OpcionAcceso }) => {
      if (opcion === 'sin-acceso') await copilotoApi.quitarAcceso(userId);
      else await copilotoApi.darAcceso(userId, opcion);
    },
    onSuccess: (_r, { opcion }) => {
      // La misma entrada se ve en "Quién le puede hablar": se refrescan las dos.
      void qc.invalidateQueries({ queryKey: ['copiloto', 'accesos'] });
      void qc.invalidateQueries({ queryKey: ['copiloto', 'usuarios'] });
      toast.success(opcion === 'sin-acceso' ? 'Acceso al asistente quitado' : 'Acceso al asistente guardado');
    },
    // El server dice por qué (p. ej. el acceso del OWNER): se muestra tal cual.
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'No se pudo cambiar el acceso'),
  });

  function elegirAcceso(u: User, opcion: OpcionAcceso) {
    const cambio = cambioDeAcceso(accesoDe(u.id)?.acceso ?? null, opcion);
    if (!cambio) return;
    if (cambio.tipo === 'dar' && cambio.confirmar) {
      setDuenoPorConfirmar(u);
      return;
    }
    cambiarAcceso.mutate({ userId: u.id, opcion });
  }

  const changeRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: Role }) => usersApi.updateRole(id, role),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      toast.success('Rol actualizado');
    },
    onError: () => toast.error('No se pudo cambiar el rol'),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => usersApi.deactivate(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      toast.info('Usuario desactivado');
    },
    onError: () => toast.error('No se pudo desactivar'),
  });

  const reactivate = useMutation({
    mutationFn: (id: string) => usersApi.reactivate(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      toast.success('Usuario reactivado');
    },
    onError: () => toast.error('No se pudo reactivar'),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => usersApi.revokeInvitation(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invitations'] });
      toast.info('Invitación revocada');
    },
    onError: () => toast.error('No se pudo revocar la invitación'),
  });

  // Reset por admin: la clave temporal se muestra UNA vez en un modal.
  const [tempPass, setTempPass] = useState<{ email: string; password: string } | null>(null);
  // Editar nombre/email (rediseño /users).
  const [editar, setEditar] = useState<User | null>(null);
  const resetPass = useMutation({
    mutationFn: async ({ id, email }: { id: string; email: string }) => ({
      email,
      result: await usersApi.adminResetPassword(id),
    }),
    onSuccess: ({ email, result }) => setTempPass({ email, password: result.tempPassword }),
    onError: () => toast.error('No se pudo restablecer la clave'),
  });

  const comoSeLlama = (u: User) => u.name ?? u.email;

  function pedirDesactivar(u: User) {
    setConfirmacion({
      titulo: `¿Desactivar a ${comoSeLlama(u)}?`,
      cuerpo:
        'No va a poder entrar más al panel. Lo que ya hizo se conserva, y la cuenta se puede ' +
        'reactivar cuando quiera desde esta misma lista.',
      boton: 'Sí, desactivar',
      alConfirmar: () => deactivate.mutate(u.id),
    });
  }

  function pedirRestablecerClave(u: User) {
    setConfirmacion({
      titulo: `¿Restablecer la clave de ${comoSeLlama(u)}?`,
      cuerpo:
        'Se genera una clave temporal para pasársela. La clave vieja deja de servir y se ' +
        'cierran todas las sesiones que tenga abiertas: si está trabajando, lo saca.',
      boton: 'Sí, generar la clave',
      alConfirmar: () => resetPass.mutate({ id: u.id, email: u.email }),
    });
  }

  function pedirCambiarRol(u: User, role: Role) {
    setConfirmacion({
      titulo: `¿Cambiar a ${comoSeLlama(u)} a ${roleLabel[role]}?`,
      cuerpo: queHaceElRol(role),
      boton: 'Sí, cambiar el rol',
      alConfirmar: () => changeRole.mutate({ id: u.id, role }),
    });
  }

  function pedirRevocar(inv: { id: string; email: string }) {
    setConfirmacion({
      titulo: '¿Revocar esta invitación?',
      cuerpo:
        `El enlace que se le mandó a ${inv.email} deja de funcionar. Si ya lo abrió y creó su ` +
        'cuenta, esto no la borra. Para invitarlo de nuevo hay que crear otra invitación.',
      boton: 'Sí, revocar',
      alConfirmar: () => revoke.mutate(inv.id),
    });
  }

  function copyInviteLink(token: string) {
    const link = `${window.location.origin}/invite/${token}`;
    void navigator.clipboard
      .writeText(link)
      .then(() => toast.success('Enlace copiado — mándelo por WhatsApp'))
      .catch(() => toast.error(`No se pudo copiar; el enlace es: ${link}`));
  }

  const items = users?.items ?? [];
  const visibles = filtrarUsuarios(items, { busqueda: buscar, rol: rolFiltrado, estado });

  /**
   * Las filas van en columnas fijas —persona, rol, asistente, acciones— para
   * que se puedan comparar de arriba abajo. Antes era un `flex-wrap`: cada fila
   * acomodaba sus cosas donde le entraban y el selector de rol terminaba en una
   * posición distinta en cada línea.
   *
   * En un teléfono no entran cuatro columnas, así que la lista se corre en
   * horizontal en vez de desarmarse: alineadas o nada, que es el punto.
   */
  const columnas = veAccesos
    ? 'grid-cols-[minmax(14rem,1fr)_10rem_17rem_9rem]'
    : 'grid-cols-[minmax(14rem,1fr)_10rem_9rem]';
  const anchoMinimo = veAccesos ? 'min-w-[58rem]' : 'min-w-[40rem]';

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Usuarios"
        helpKey="users"
        description="Gestiona el equipo, sus roles e invitaciones."
        action={
          <Button data-guia="users.invitar" onClick={() => setInviteOpen(true)}>
            <UserPlus className="h-4 w-4" /> Invitar usuario
          </Button>
        }
      />

      {/* Buscador y filtros: antes no había ninguno y la pantalla traía las 100
          cuentas de una. Con veinte, encontrar a alguien era leer la lista. */}
      <div className="flex flex-wrap items-center gap-3">
        <div data-guia="users.buscador" className="w-full max-w-sm">
          <CajaDeBusqueda
            valor={buscar}
            placeholder="Buscar por nombre, email o teléfono"
            onChange={(v) => setFiltros({ buscar: v || undefined })}
          />
        </div>
        <div className="w-44">
          <Select
            aria-label="Filtrar por rol"
            options={OPCIONES_FILTRO_ROL}
            value={rolFiltrado}
            onChange={(e) => setFiltros({ rol: e.target.value || undefined })}
          />
        </div>
        <div className="w-48">
          <Select
            aria-label="Filtrar por activos o inactivos"
            options={OPCIONES_FILTRO_ESTADO}
            value={estado}
            onChange={(e) =>
              setFiltros({
                estado: e.target.value === 'todos' ? undefined : (e.target.value as EstadoUsuarios),
              })
            }
          />
        </div>
        <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
          {contadorDeUsuarios(visibles.length)}
        </span>
        {hayFiltros && (
          <Button variant="ghost" size="sm" onClick={limpiarFiltros}>
            Quitar los filtros
          </Button>
        )}
      </div>

      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <Cargando />
          ) : items.length === 0 ? (
            <EmptyState
              icon={UserCog}
              title="Sin usuarios"
              description="Invite a su equipo con el botón de arriba; el enlace se puede mandar por WhatsApp."
              className="m-5"
            />
          ) : visibles.length === 0 ? (
            // A7 · El vacío dice qué hacer, y no se confunde con "no hay nadie".
            <EmptyState
              icon={UserCog}
              title="Ningún usuario con estos filtros"
              description="Pruebe borrando lo que escribió, o poniendo el rol en «Todos los roles»."
              className="m-5"
            />
          ) : (
            <div className="overflow-x-auto">
              <div className={anchoMinimo}>
                <div
                  className={cn(
                    'grid items-center gap-3 border-b border-surface-border px-5 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:border-slate-800',
                    columnas,
                  )}
                >
                  <span>Persona</span>
                  <span>Rol</span>
                  {veAccesos && <span className="text-right">Acceso al asistente</span>}
                  <span className="text-right">Acciones</span>
                </div>
                <ul className="divide-y divide-surface-border dark:divide-slate-800">
                  {visibles.map((u) => (
                    <li key={u.id} className={cn('grid items-center gap-3 px-5 py-3', columnas)}>
                      {/* [persona] */}
                      <div className="flex min-w-0 items-center gap-3">
                        <Avatar name={u.name ?? u.email} size="md" />
                        <div className="min-w-0">
                          <p className="flex items-center gap-2 text-sm font-medium text-slate-900 dark:text-slate-100">
                            <span className="truncate">{u.name ?? '—'}</span>
                            {u.id === meId && <span className="text-xs text-slate-400">(tú)</span>}
                            {!u.isActive && <Badge variant="danger">Inactivo</Badge>}
                          </p>
                          <p className="truncate text-xs text-slate-400">
                            {u.email}
                            {u.telefono ? ` · ${u.telefono}` : ''}
                          </p>
                        </div>
                      </div>

                      {/* [rol] */}
                      <Select
                        aria-label={`Rol de ${comoSeLlama(u)}`}
                        value={u.role}
                        options={OPCIONES_DE_ROL}
                        disabled={u.id === meId || changeRole.isPending}
                        onChange={(e) => pedirCambiarRol(u, e.target.value as Role)}
                      />

                      {/* [asistente] — el control es el mismo; solo cambió de lugar. */}
                      {veAccesos && (
                        <div className="flex items-center justify-end gap-2">
                          <div className="min-w-0 text-right text-xs leading-tight">
                            <p className="truncate font-medium text-slate-700 dark:text-slate-200">
                              {etiquetaDeAcceso(accesoDe(u.id)?.acceso ?? null)}
                            </p>
                            <p className="truncate text-slate-400">
                              {usoCorto(accesoDe(u.id)?.mensajes30d ?? 0)}
                            </p>
                          </div>
                          <Select
                            className="w-32"
                            aria-label={`Acceso al asistente de ${u.name ?? u.email}`}
                            title="Acceso al asistente"
                            value={opcionActual(accesoDe(u.id)?.acceso ?? null)}
                            options={opcionesDeAcceso(miRol, u.role)}
                            disabled={accesos.isLoading || cambiarAcceso.isPending}
                            onChange={(e) => elegirAcceso(u, e.target.value as OpcionAcceso)}
                          />
                        </div>
                      )}

                      {/* [acciones] */}
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Editar nombre, email y teléfono"
                          onClick={() => setEditar(u)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Restablecer clave (genera una temporal)"
                          disabled={u.id === meId}
                          onClick={() => pedirRestablecerClave(u)}
                          loading={resetPass.isPending && resetPass.variables?.id === u.id}
                        >
                          <KeyRound className="h-4 w-4" />
                        </Button>
                        {u.isActive ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Desactivar usuario"
                            disabled={u.id === meId}
                            onClick={() => pedirDesactivar(u)}
                            loading={deactivate.isPending && deactivate.variables === u.id}
                          >
                            <Ban className="h-4 w-4 text-danger" />
                          </Button>
                        ) : (
                          // Reactivar NO pregunta: devolver el acceso no le quita
                          // nada a nadie, y equivocarse se deshace en un clic.
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => reactivate.mutate(u.id)}
                            loading={reactivate.isPending && reactivate.variables === u.id}
                          >
                            Reactivar
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {invitations && invitations.length > 0 && (
        <Card>
          <CardBody className="p-0">
            <div className="border-b border-surface-border px-5 py-3 dark:border-slate-800">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Invitaciones pendientes</h2>
            </div>
            <ul className="divide-y divide-surface-border dark:divide-slate-800">
              {invitations.map((inv) => (
                <li key={inv.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                    <Mail className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-slate-800 dark:text-slate-100">{inv.email}</p>
                  </div>
                  <Badge variant="warning">{roleLabel[inv.role]}</Badge>
                  <Button variant="outline" size="sm" onClick={() => copyInviteLink(inv.token)}>
                    <Copy className="mr-1 h-3.5 w-3.5" /> Copiar enlace
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Revocar invitación"
                    onClick={() => pedirRevocar(inv)}
                    loading={revoke.isPending && revoke.variables === inv.id}
                  >
                    <Trash2 className="h-4 w-4 text-danger" />
                  </Button>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      <InviteModal open={inviteOpen} onClose={() => setInviteOpen(false)} />

      <EditarUsuarioModal user={editar} onClose={() => setEditar(null)} />

      {/* Las cuatro cosas que antes se aplicaban de una: desactivar, restablecer
          la clave, cambiar el rol y revocar una invitación. */}
      <Modal
        open={confirmacion !== null}
        onClose={() => setConfirmacion(null)}
        title={confirmacion?.titulo ?? ''}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmacion(null)}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                confirmacion?.alConfirmar();
                setConfirmacion(null);
              }}
            >
              {confirmacion?.boton ?? 'Confirmar'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">{confirmacion?.cuerpo}</p>
      </Modal>

      {/* Dar "Dueño" se confirma: es dejar ver la plata y lo que hablan los demás. */}
      <Modal
        open={duenoPorConfirmar !== null}
        onClose={() => setDuenoPorConfirmar(null)}
        title={`¿Darle a ${duenoPorConfirmar?.name ?? duenoPorConfirmar?.email ?? ''} acceso de Dueño?`}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDuenoPorConfirmar(null)}>
              Cancelar
            </Button>
            <Button
              loading={cambiarAcceso.isPending}
              onClick={() => {
                if (!duenoPorConfirmar) return;
                cambiarAcceso.mutate(
                  { userId: duenoPorConfirmar.id, opcion: 'dueno' },
                  { onSettled: () => setDuenoPorConfirmar(null) },
                );
              }}
            >
              Sí, darle acceso de Dueño
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">{AVISO_CONFIRMAR_DUENO}</p>
      </Modal>

      {/* Clave temporal generada: se muestra UNA vez para pasarla por WhatsApp. */}
      <Modal
        open={tempPass !== null}
        onClose={() => setTempPass(null)}
        title="Clave temporal generada"
        description={`Pásesela a ${tempPass?.email ?? ''} por WhatsApp. No se vuelve a mostrar.`}
        footer={
          <Button onClick={() => setTempPass(null)}>Listo, la copié</Button>
        }
      >
        <div className="space-y-3">
          <p className="rounded-md bg-slate-100 px-4 py-3 text-center font-mono text-lg font-semibold tracking-wide text-slate-900 dark:bg-slate-800 dark:text-slate-100">
            {tempPass?.password}
          </p>
          <p className="text-xs text-slate-400">
            Las sesiones anteriores de ese usuario quedaron cerradas. Recomiéndele cambiar
            la clave al entrar.
          </p>
        </div>
      </Modal>
    </div>
  );
}
